import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import AudioPlayer from "../components/AudioPlayer";
import SpeechRecorder from "../components/SpeechRecorder";
import WritingInput from "../components/WritingInput";
import {
  ArrowRight, ArrowLeft, Headphones, Mic, BookOpen, PenTool,
  CheckCircle, Play, GraduationCap, BarChart3, Users, Send,
  Sparkles, ChevronRight, ChevronLeft, School, UserCheck, AlertCircle, Info
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import PreTestInstructions from "../components/PreTestInstructions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const READING_CAN_DO = {
  1: { level: "Entering", desc: "At Level 1, students can identify familiar words when paired with pictures and match icons or images with word labels." },
  2: { level: "Emerging", desc: "At Level 2, students can identify the main topic of simple, illustrated text and follow written directions with visual support." },
  3: { level: "Developing", desc: "At Level 3, students can identify main ideas and supporting details in grade-level text with some support and use context clues for unfamiliar words." },
  4: { level: "Expanding", desc: "At Level 4, students can read and interpret a variety of genres with moderate support and make inferences from text." },
  5: { level: "Bridging", desc: "At Level 5, students can read and analyze grade-level text with minimal support and evaluate arguments in persuasive texts." },
  6: { level: "Reaching", desc: "At Level 6, students comprehend grade-level text independently across all content areas and analyze complex texts for theme and structure." },
};

const GRADES = [
  { value: "K", label: "Kindergarten", icon: "K", color: "bg-purple-100 text-purple-700 border-purple-300" },
  { value: "1", label: "Grade 1", icon: "1", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: "2", label: "Grade 2", icon: "2", color: "bg-cyan-100 text-cyan-700 border-cyan-300" },
  { value: "3-4", label: "Grades 3-4", icon: "3-4", color: "bg-green-100 text-green-700 border-green-300" },
  { value: "5-6", label: "Grades 5-6", icon: "5-6", color: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  { value: "7-8", label: "Grades 7-8", icon: "7-8", color: "bg-orange-100 text-orange-700 border-orange-300" },
  { value: "9-12", label: "Grades 9-12", icon: "HS", color: "bg-red-100 text-red-700 border-red-300" },
];

const MODALITIES = [
  { key: "listening", label: "Listening", icon: Headphones, color: "text-purple-600", bg: "bg-purple-100" },
  { key: "speaking", label: "Speaking", icon: Mic, color: "text-blue-600", bg: "bg-blue-100" },
  { key: "reading", label: "Reading", icon: BookOpen, color: "text-green-600", bg: "bg-green-100" },
  { key: "writing", label: "Writing", icon: PenTool, color: "text-orange-600", bg: "bg-orange-100" },
];

const WIDA_COLORS = {
  1: { bg: "bg-red-100", text: "text-red-700", fill: "bg-red-500" },
  2: { bg: "bg-orange-100", text: "text-orange-700", fill: "bg-orange-500" },
  3: { bg: "bg-yellow-100", text: "text-yellow-700", fill: "bg-yellow-500" },
  4: { bg: "bg-blue-100", text: "text-blue-700", fill: "bg-blue-500" },
  5: { bg: "bg-indigo-100", text: "text-indigo-700", fill: "bg-indigo-500" },
  6: { bg: "bg-green-100", text: "text-green-700", fill: "bg-green-500" },
};

const ROLE_OPTIONS = [
  "Teacher", "ENL Coordinator", "Principal", "Assistant Principal",
  "Curriculum Director", "District Administrator", "Other"
];

// ─── Phase 0: Grade Selection ───────────────────────────────────
const GradePickerPhase = ({ onStart }) => {
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [visitorName, setVisitorName] = useState("");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#002FA7] text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 text-sm font-semibold mb-6">
            <Play size={16} /> FREE TRIAL
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Try MOSAIC — Free
          </h1>
          <p className="text-lg text-blue-100 max-w-xl mx-auto">
            Experience a mini language-proficiency assessment. Pick a grade, answer a few questions,
            and see how MOSAIC scores and reports student proficiency.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Name */}
        <div className="bg-white border-2 border-gray-200 p-6 mb-8">
          <Label className="text-sm font-semibold mb-2 block">Your First Name</Label>
          <Input
            placeholder="e.g., Jane"
            value={visitorName}
            onChange={(e) => setVisitorName(e.target.value)}
            className="h-12 border-2 border-gray-300 focus:border-[#002FA7] rounded-none max-w-sm"
            data-testid="demo-visitor-name"
          />
          <p className="text-xs text-gray-400 mt-2">Just so we can personalize your demo experience.</p>
        </div>

        {/* Grade Selection */}
        <div className="bg-white border-2 border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <GraduationCap size={20} className="text-[#002FA7]" />
            Select a Grade Level
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="demo-grade-picker">
            {GRADES.map((g) => (
              <button
                key={g.value}
                onClick={() => setSelectedGrade(g.value)}
                className={`p-4 border-2 text-center transition-all ${
                  selectedGrade === g.value
                    ? "border-[#002FA7] bg-blue-50 ring-2 ring-[#002FA7] ring-offset-1"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`demo-grade-${g.value}`}
              >
                <div className={`w-12 h-12 ${g.color} border-2 flex items-center justify-center font-bold text-lg mx-auto mb-2`}>
                  {g.icon}
                </div>
                <div className="font-semibold text-sm">{g.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* What to Expect */}
        <div className="bg-blue-50 border-2 border-blue-200 p-6 mb-8">
          <h3 className="font-bold mb-3">What to Expect</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
            <div className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> 12 questions across 4 modalities</div>
            <div className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Takes about 5-8 minutes</div>
            <div className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> Proficiency score at the end</div>
            <div className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" /> No login or account required</div>
          </div>
        </div>

        <Button
          onClick={() => onStart(selectedGrade, visitorName.trim() || "Visitor")}
          disabled={!selectedGrade}
          className="w-full h-14 text-lg btn-sharp-primary"
          data-testid="demo-start-btn"
        >
          <Play size={20} className="mr-2" />
          Start Free Trial — Grade {selectedGrade || "..."}
        </Button>
      </div>
    </div>
  );
};

// ─── Phase 1: Mini Exam ─────────────────────────────────────────
const ExamPhase = ({ questions, gradeLevel, visitorName, onComplete }) => {
  const [responses, setResponses] = useState({});
  const [currentModality, setCurrentModality] = useState("listening");
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answerImageManifest, setAnswerImageManifest] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const isK2 = ["K", "1", "2"].includes(gradeLevel);
  const isMid = ["3-4", "5-6"].includes(gradeLevel);

  // Elapsed time tracker (informational only — WIDA ACCESS is untimed)
  useEffect(() => {
    const interval = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // v6.32.224 — Attribution beacon. If the visitor arrived via a tagged
  // source (e.g. ?src=flyer from the printed Principal Sneak-Peek Flyer
  // QR code), record one deduped hit per (visitor + source + day). The
  // backend sets a HttpOnly cookie so a later demo-lead submission can
  // be attributed back to this scan without storing any PII here.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const src = params.get('src');
      if (!src) return;
      axios.post(
        `${API}/attribution/visit`,
        { source: src },
        { withCredentials: true }
      ).catch(() => {});  // Best-effort: never block the demo flow.
    } catch {
      // Defensive: attribution must never break the page.
    }
  }, []);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };

  // Fetch answer image manifest for K-2 pictograms
  useEffect(() => {
    if (isK2) {
      axios.get(`${API}/blm/answer-images/manifest`)
        .then(res => setAnswerImageManifest(res.data?.manifest || {}))
        .catch(() => {});
    }
  }, [isK2]);

  const modalityQs = questions.filter((q) => q.modality === currentModality);
  const currentQ = modalityQs[currentQIndex];
  const totalAnswered = Object.keys(responses).length;
  const progress = questions.length > 0 ? (totalAnswered / questions.length) * 100 : 0;

  // WIDA Part tracking — group questions by Big Picture context
  const getPartInfo = () => {
    if (!currentQ) return { partLabel: '', partContext: '', isFirstInPart: false, isLastInPart: false, partIndex: 0 };
    const ctx = currentQ.big_picture_context || 'General';
    // Find all unique contexts in order for this modality
    const contexts = [];
    const seen = new Set();
    modalityQs.forEach(q => {
      const c = q.big_picture_context || 'General';
      if (!seen.has(c)) { seen.add(c); contexts.push(c); }
    });
    const partIndex = contexts.indexOf(ctx);
    const partLabel = `Part ${String.fromCharCode(65 + partIndex)}`;
    // Is this the first/last question in this Part?
    const isFirstInPart = currentQIndex === 0 || (modalityQs[currentQIndex - 1]?.big_picture_context || 'General') !== ctx;
    const isLastInPart = currentQIndex === modalityQs.length - 1 || (modalityQs[currentQIndex + 1]?.big_picture_context || 'General') !== ctx;
    return { partLabel, partContext: ctx, isFirstInPart, isLastInPart, partIndex, totalParts: contexts.length };
  };
  const partInfo = getPartInfo();

  const modIdx = MODALITIES.findIndex((m) => m.key === currentModality);
  const isLastQ = modIdx === MODALITIES.length - 1 && currentQIndex === modalityQs.length - 1;
  const isFirstQ = modIdx === 0 && currentQIndex === 0;

  const handleNext = () => {
    if (currentQIndex < modalityQs.length - 1) {
      setCurrentQIndex((p) => p + 1);
    } else if (modIdx < MODALITIES.length - 1) {
      setCurrentModality(MODALITIES[modIdx + 1].key);
      setCurrentQIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentQIndex > 0) {
      setCurrentQIndex((p) => p - 1);
    } else if (modIdx > 0) {
      const prevMod = MODALITIES[modIdx - 1].key;
      const prevQs = questions.filter((q) => q.modality === prevMod);
      setCurrentModality(prevMod);
      setCurrentQIndex(prevQs.length - 1);
    }
  };

  const handleSubmit = () => onComplete(responses);

  const CurrentIcon = MODALITIES.find((m) => m.key === currentModality)?.icon || Headphones;

  if (!currentQ) return <div className="flex items-center justify-center min-h-screen"><div className="loading-spinner" /></div>;

  return (
    <div className={`min-h-screen bg-gray-50 ${isK2 ? "k2-mode" : isMid ? "mid-mode" : ""}`}>
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#002FA7] text-white px-3 py-1 text-xs font-bold">DEMO</div>
            <span className="font-semibold text-sm">Grade {gradeLevel}</span>
            <span className="text-gray-400 text-sm">| {visitorName}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-mono" data-testid="demo-elapsed-time" title="Elapsed time (WIDA ACCESS is untimed)">{formatTime(elapsedSeconds)}</span>
            <span className="text-xs text-gray-500">{totalAnswered}/{questions.length} answered</span>
            <Progress value={progress} className="w-24 h-2" />
          </div>
        </div>
      </div>

      {/* Modality Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {MODALITIES.map((mod) => {
            const count = questions.filter((q) => q.modality === mod.key).length;
            const answered = questions.filter((q) => q.modality === mod.key && responses[q.id]).length;
            const Icon = mod.icon;
            return (
              <button
                key={mod.key}
                onClick={() => { setCurrentModality(mod.key); setCurrentQIndex(0); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  currentModality === mod.key
                    ? "border-[#002FA7] text-[#002FA7]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`demo-tab-${mod.key}`}
              >
                <Icon size={16} />
                {mod.label}
                <span className="text-xs opacity-60">{answered}/{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* WIDA Part Banner — shown at start of each Part */}
        {partInfo.isFirstInPart && currentQ && (
          <div className="bg-gray-900 text-white px-5 py-3 mb-4 flex items-center justify-between" data-testid="wida-part-banner">
            <div className="flex items-center gap-3">
              <span className="font-bold text-base tracking-wide">{partInfo.partLabel}: {partInfo.partContext}</span>
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">
              Part {partInfo.partIndex + 1} of {partInfo.totalParts}
            </span>
          </div>
        )}

        <div className="bg-white border-2 border-gray-200 p-6 sm:p-8">
          {/* Teacher Reads Aloud Banner */}
          {currentQ.teacher_reads && (
            <div className="bg-amber-50 border-2 border-amber-300 p-4 mb-4 flex items-start gap-3" data-testid="teacher-reads-banner">
              <div className="w-8 h-8 bg-amber-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <div>
                <div className="font-bold text-amber-800 text-sm">Teacher: Read Aloud</div>
                <p className="text-xs text-amber-700 mt-0.5">This question is designed to be read aloud by the teacher. Please read the passage and question to the student.</p>
              </div>
            </div>
          )}

          {/* Picture Prompt (Writing, Speaking, or any question with graphic_support_url) */}
          {currentQ.graphic_support_url && !(currentQ.bp_image_url && (currentQ.modality === 'listening' || currentQ.modality === 'reading')) && (
            <div className="mb-4 border-2 border-blue-200 overflow-hidden" data-testid="question-picture-prompt">
              <img src={currentQ.graphic_support_url} alt="Picture prompt" className={`w-full object-contain bg-gray-50 ${isK2 ? 'h-48 sm:h-64' : 'h-40 sm:h-52'}`} />
              {currentQ.modality === 'writing' && (
                <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above. Use it to help you write.</div>
              )}
              {currentQ.modality === 'speaking' && (
                <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above. Use it to help you respond.</div>
              )}
              {(currentQ.modality === 'reading' || currentQ.modality === 'listening') && (
                <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above.</div>
              )}
            </div>
          )}

          {/* Side-by-side layout for questions with Big Picture images (Listening + Reading K-2) */}
          {(currentQ.modality === "listening" || currentQ.modality === "reading") && (currentQ.bp_image_url || (!currentQ.graphic_support_url && currentQ.bp_image_url)) ? (
            <div className={`flex flex-col ${isK2 ? "" : "lg:flex-row"} gap-4`}>
              {/* Left: Image + Context */}
              <div className={`${isK2 ? "w-full" : "lg:w-1/2"} border-2 border-gray-200 bg-white overflow-hidden flex-shrink-0`}>
                <img
                  src={currentQ.bp_image_url}
                  alt={currentQ.big_picture_context || "Scene"}
                  className={`w-full object-contain bg-gray-50 ${isK2 ? "h-64 sm:h-80 lg:h-96" : "h-48 sm:h-64 lg:h-72"}`}
                  data-testid="big-picture-image"
                />
                <div className="p-4 border-t-2 border-gray-200">
                  {currentQ.modality === "listening" ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600">
                          BIG IDEA
                        </div>
                        <div className="flex-1 h-px bg-blue-200" />
                      </div>
                      {currentQ.stimulus && (
                        <p className="text-gray-800 leading-relaxed text-sm">
                          {currentQ.stimulus.replace(/^\[Big Picture \d+: /, '').replace(/\]$/, '')}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 ${isK2 && currentQ.wida_level <= 2 ? 'text-sm' : ''}`}>
                          {currentQ.wida_level === 1
                            ? 'READ THE WORD'
                            : currentQ.wida_level === 2
                              ? 'READ THE SENTENCE'
                              : 'READ THE PASSAGE'}
                        </div>
                        {currentQ.wida_level && READING_CAN_DO[currentQ.wida_level] && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 border border-emerald-300 text-emerald-700 text-[10px] font-bold tracking-wide uppercase hover:bg-emerald-200 transition-colors cursor-help" data-testid="reading-can-do-badge">
                                  <Info size={12} />
                                  L{currentQ.wida_level} {READING_CAN_DO[currentQ.wida_level].level}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs bg-emerald-900 text-white px-4 py-3 text-sm leading-relaxed">
                                <p className="font-bold mb-1">Reading Proficiency — Level {currentQ.wida_level}</p>
                                <p>{READING_CAN_DO[currentQ.wida_level].desc}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <div className="flex-1 h-px bg-emerald-200" />
                      </div>
                      {currentQ.stimulus && currentQ.wida_level > 2 && (
                        <p className={`text-gray-800 leading-relaxed ${isK2 ? 'text-base' : 'text-sm'}`}>
                          {currentQ.stimulus
                            .replace(/^\[Passage: [^\]]+\]\s*/, '')
                            .replace(/\[Image:[^\]]+\]/g, '')
                            .replace(/^Look at the picture\.?\s*/i, '')
                            .replace(/^Look at the picture of the parade\.?\s*/i, '')}
                        </p>
                      )}
                      {currentQ.stimulus && currentQ.wida_level <= 2 && (
                        <p className={`text-gray-500 italic mt-1 ${isK2 ? 'text-base' : 'text-sm'}`}>
                          {currentQ.stimulus}
                        </p>
                      )}
                    </>
                  )}
                  {currentQ.big_picture_context && (
                    <div className="mt-2 px-2 py-1 bg-blue-50 border border-blue-200 inline-block">
                      <span className="text-[10px] font-bold text-blue-700 tracking-wide uppercase">Scene: </span>
                      <span className="text-xs text-blue-800">{currentQ.big_picture_context}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Right: Audio (listening only) + Question + Answers */}
              <div className="lg:w-1/2 flex flex-col">
                {currentQ.modality === "listening" && (
                  <div className="mb-4">
                    <AudioPlayer questionId={currentQ.id} />
                  </div>
                )}
                <div className="flex items-start gap-3 mb-4">
                  <div className={`w-8 h-8 flex items-center justify-center text-sm font-bold ${
                    MODALITIES.find(m => m.key === currentModality)?.bg || "bg-gray-100"
                  } ${MODALITIES.find(m => m.key === currentModality)?.color || ""}`}>
                    {currentQIndex + 1}
                  </div>
                  <p className={`font-semibold flex-1 ${isK2 && currentQ.modality === 'reading' && currentQ.wida_level <= 2 ? "text-3xl leading-relaxed" : isK2 ? "text-2xl leading-relaxed" : "text-base"}`}>
                    {currentQ.question_text}
                  </p>
                </div>
                {currentQ.question_type === "multiple_choice" && currentQ.options && (
                  <RadioGroup
                    value={responses[currentQ.id] || ""}
                    onValueChange={(val) => setResponses((p) => ({ ...p, [currentQ.id]: val }))}
                    className={isK2 ? "grid grid-cols-2 gap-3" : "space-y-3"}
                    data-testid="demo-mc-options"
                  >
                    {currentQ.options.map((opt, i) => {
                      const pictogramUrl = isK2 ? answerImageManifest[opt.trim()] : null;
                      return (
                      <div
                        key={i}
                        className={`flex items-center cursor-pointer transition-all ${
                          responses[currentQ.id] === opt
                            ? "border-[#002FA7] bg-blue-50 border-2 shadow-md"
                            : "border-gray-200 hover:border-gray-300 border-2"
                        } ${isK2
                            ? 'flex-col items-center justify-center text-center min-h-[120px] p-3 gap-1'
                            : 'space-x-3 p-4'
                        }`}
                        onClick={() => setResponses((p) => ({ ...p, [currentQ.id]: opt }))}
                        data-testid={`demo-option-${i}`}
                      >
                        {isK2 ? (
                          <>
                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                              responses[currentQ.id] === opt
                                ? "border-[#002FA7] bg-[#002FA7] text-white"
                                : "border-gray-400 text-gray-600"
                            }`}>
                              {String.fromCharCode(65 + i)}
                            </div>
                            {pictogramUrl && (
                              <img
                                src={pictogramUrl}
                                alt={opt}
                                className="w-14 h-14 object-contain"
                                data-testid={`demo-option-img-${i}`}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span className="text-lg font-semibold mt-1">{opt}</span>
                          </>
                        ) : (
                          <>
                            <RadioGroupItem value={opt} id={`opt-${i}`} />
                            <Label htmlFor={`opt-${i}`} className="cursor-pointer flex-1">
                              {opt}
                            </Label>
                          </>
                        )}
                      </div>
                      );
                    })}
                  </RadioGroup>
                )}
              </div>
            </div>
          ) : (
          <>
          {/* Non-listening image layout (stacked, original) */}
          {currentQ.modality === "listening" && (
            <div className="mb-4">
              <AudioPlayer questionId={currentQ.id} />
            </div>
          )}

          {/* Passage for reading (non-image version) */}
          {currentQ.passage && !currentQ.bp_image_url && (
            <div className="bg-gray-50 border border-gray-200 p-4 mb-6 text-sm leading-relaxed">
              <p className="font-semibold text-xs text-gray-500 mb-2 uppercase">Read this passage:</p>
              {currentQ.passage}
            </div>
          )}

          {/* ═══ WIDA "S" Sample Question Banner (first question per modality) ═══ */}
          {currentQIndex === 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="demo-sample-banner">
              <div className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">S</div>
              <span className="text-xs text-amber-800"><strong>Sample Question:</strong> This question is done together with your teacher as guided practice.</span>
            </div>
          )}

          {/* ═══ WIDA Labeled Images for Writing (Picture A, Picture B) ═══ */}
          {currentQ.modality === 'writing' && currentQ.graphic_support_url && (
            <div className="mb-4 border border-gray-200 rounded-lg p-4" data-testid="demo-labeled-images">
              <div className="flex gap-4 items-start">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-600 mb-1">Picture A</div>
                  <div className={`border-2 border-blue-200 rounded-lg overflow-hidden ${isK2 ? 'w-44 h-44' : 'w-36 h-36'}`}>
                    <img src={currentQ.graphic_support_url} alt="Picture A" className="w-full h-full object-contain bg-gray-50" />
                  </div>
                </div>
                {currentQ.bp_image_url && currentQ.bp_image_url !== currentQ.graphic_support_url && (
                  <div className="text-center">
                    <div className="text-xs font-bold text-gray-600 mb-1">Picture B</div>
                    <div className={`border-2 border-blue-200 rounded-lg overflow-hidden ${isK2 ? 'w-44 h-44' : 'w-36 h-36'}`}>
                      <img src={currentQ.bp_image_url} alt="Picture B" className="w-full h-full object-contain bg-gray-50" />
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-blue-600 mt-2 italic">Look at the picture{currentQ.bp_image_url && currentQ.bp_image_url !== currentQ.graphic_support_url ? 's' : ''} above. Use {currentQ.bp_image_url && currentQ.bp_image_url !== currentQ.graphic_support_url ? 'them' : 'it'} to help you write.</p>
            </div>
          )}

          {/* Per-question support image (shown alongside Big Picture for added visual context) */}
          {currentQ.graphic_support_url && currentQ.bp_image_url && (currentQ.modality === 'listening' || currentQ.modality === 'reading') && (
            <div className="mb-4 flex items-center gap-3" data-testid="question-support-image">
              <div className={`border-2 border-blue-200 rounded-lg overflow-hidden flex-shrink-0 ${isK2 ? 'w-40 h-40' : 'w-28 h-28'}`}>
                <img src={currentQ.graphic_support_url} alt="Question support" className="w-full h-full object-contain bg-gray-50" />
              </div>
              <div className="text-xs text-blue-600 font-medium italic">Look at this picture to help answer the question.</div>
            </div>
          )}

          {/* Question text — skip if already in side-by-side panel */}
          {!((currentQ.modality === "listening" || currentQ.modality === "reading") && currentQ.bp_image_url) && (
          <div className="flex items-start gap-3 mb-6">
            <div className={`w-8 h-8 flex items-center justify-center text-sm font-bold ${
              MODALITIES.find(m => m.key === currentModality)?.bg || "bg-gray-100"
            } ${MODALITIES.find(m => m.key === currentModality)?.color || ""}`}>
              {currentQIndex + 1}
            </div>
            <p className={`font-semibold flex-1 ${isK2 ? "text-2xl leading-relaxed" : "text-base"}`}>
              {currentQ.question_text}
            </p>
          </div>
          )}

          {/* Support prompts */}
          {currentQ.support_prompts && currentQ.support_prompts.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 p-3 mb-6 text-sm">
              <p className="font-semibold text-blue-800 text-xs mb-1">Scaffolds:</p>
              <ul className="space-y-1 text-blue-700">
                {currentQ.support_prompts.map((sp, i) => <li key={i}>- {sp}</li>)}
              </ul>
            </div>
          )}

          {/* Answer area — skip MC if already in side-by-side panel */}
          {!((currentQ.modality === "listening" || currentQ.modality === "reading") && currentQ.bp_image_url && currentQ.question_type === "multiple_choice") && (
          <>
          {currentQ.question_type === "multiple_choice" && currentQ.options ? (
            <RadioGroup
              value={responses[currentQ.id] || ""}
              onValueChange={(val) => setResponses((p) => ({ ...p, [currentQ.id]: val }))}
              className="space-y-3"
              data-testid="demo-mc-options"
            >
              {currentQ.options.map((opt, i) => (
                <div
                  key={i}
                  className={`flex items-center space-x-3 p-4 border-2 cursor-pointer transition-colors ${
                    responses[currentQ.id] === opt
                      ? "border-[#002FA7] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <RadioGroupItem value={opt} id={`opt-${i}`} />
                  <Label htmlFor={`opt-${i}`} className={`cursor-pointer flex-1 ${isK2 ? "text-xl" : ""}`}>
                    {opt}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          ) : currentQ.modality === "speaking" ? (
            <div className="space-y-4">
              <SpeechRecorder
                onTranscription={(text) => {
                  if (text) setResponses((p) => ({ ...p, [currentQ.id]: text }));
                }}
                questionContext="Record your spoken response using the microphone button below. Your speech will be transcribed automatically."
                disabled={false}
              />
              <div className="text-xs text-gray-400">Or type your response below:</div>
              <Textarea
                placeholder="Type what you would say out loud..."
                value={responses[currentQ.id] || ""}
                onChange={(e) => setResponses((p) => ({ ...p, [currentQ.id]: e.target.value }))}
                className={`border-2 border-gray-300 focus:border-[#002FA7] rounded-none resize-none ${
                  isK2 ? "min-h-[140px] text-xl" : "min-h-[100px]"
                }`}
                data-testid="demo-text-answer"
              />
            </div>
          ) : ["k_labeling", "k_sentence_completion", "g1_sentences", "g2_short_narrative"].includes(currentQ.question_type) ? (
            <WritingInput
              key={currentQ.id}
              question={currentQ}
              value={responses[currentQ.id] || ""}
              onChange={(val) => setResponses((p) => ({ ...p, [currentQ.id]: val }))}
              isK2={isK2}
              isMid={isMid}
            />
          ) : (
            <div className="space-y-3">
              {/* ═══ WIDA "Plan Your Writing" (Gr 3+, not for lists) ═══ */}
              {!isK2 && currentQ.modality === 'writing' && !currentQ.question_text?.toLowerCase().includes('list') && !currentQ.question_text?.toLowerCase().includes('name the') && (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50" data-testid="demo-plan-writing">
                  <div className="text-xs font-bold text-gray-600 mb-1">Plan your writing</div>
                  <p className="text-[10px] text-gray-400 mb-2">Use this space to organize your ideas before writing.</p>
                  <Textarea placeholder="Notes, outline, web, or graphic organizer..." className="min-h-[60px] text-sm border-gray-200 bg-white rounded-none resize-none" />
                </div>
              )}
              {/* ═══ WIDA Sentence/Word Count Guidance ═══ */}
              {currentQ.modality === 'writing' && (
                <div className="text-xs font-bold text-gray-600" data-testid="demo-writing-guidance">
                  {currentQ.question_text?.toLowerCase().includes('list') || currentQ.question_text?.toLowerCase().includes('name the')
                    ? 'Write your list below:'
                    : currentQ.question_text?.toLowerCase().includes('describe')
                    ? 'Write 2 to 4 sentences.'
                    : isK2 ? 'Write 1 to 3 sentences.'
                    : ['7-8','9-12'].includes(gradeLevel) ? 'Write 5 to 8 sentences.'
                    : 'Write 3 to 5 sentences.'}
                </div>
              )}
              <Textarea
                placeholder="Type your response here..."
                value={responses[currentQ.id] || ""}
                onChange={(e) => setResponses((p) => ({ ...p, [currentQ.id]: e.target.value }))}
                className={`border-2 border-gray-300 focus:border-[#002FA7] rounded-none resize-none ${
                  isK2 ? "min-h-[140px] text-xl" : "min-h-[100px]"
                }`}
                data-testid="demo-text-answer"
              />
              {/* ═══ WIDA Self-Assessment Checklist (Gr 3+, not for lists) ═══ */}
              {!isK2 && currentQ.modality === 'writing' && !currentQ.question_text?.toLowerCase().includes('list') && !currentQ.question_text?.toLowerCase().includes('name the') && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="demo-self-assessment">
                  <div className="text-xs font-bold text-gray-700 mb-2">Now check your writing. Ask yourself:</div>
                  <div className="grid grid-cols-2 gap-1">
                    {['Did I write a beginning?','Did I support my points with details?','Did I write an ending?','Did I choose the best words?','Did I organize my writing?','Did I make my ideas clear?'].map((item,i) => (
                      <label key={i} className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded border-gray-400" />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          )}
          </>
          )}
        </div>

        {/* WIDA STOP Cue — shown at end of each Part */}
        {partInfo.isLastInPart && partInfo.partIndex < partInfo.totalParts - 1 && (
          <div className="flex items-center justify-end gap-3 mt-4 mb-2" data-testid="wida-stop-cue">
            <span className="text-sm text-gray-500">End of {partInfo.partLabel}</span>
            <div className="w-10 h-10 bg-gray-800 flex items-center justify-center rotate-0" style={{ clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' }}>
              <span className="text-white text-[9px] font-bold">STOP</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            onClick={handlePrev}
            disabled={isFirstQ}
            variant="outline"
            className="btn-sharp h-12 px-6"
            data-testid="demo-prev-btn"
          >
            <ChevronLeft size={18} className="mr-1" /> Previous
          </Button>
          {isLastQ ? (
            <Button
              onClick={handleSubmit}
              className="btn-sharp-primary h-12 px-8"
              data-testid="demo-submit-btn"
            >
              <CheckCircle size={18} className="mr-1" /> See My Results
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="btn-sharp-primary h-12 px-6"
              data-testid="demo-next-btn"
            >
              Next <ChevronRight size={18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Phase 2: Results + Lead Form ───────────────────────────────
const ResultsPhase = ({ result, sessionId }) => {
  const navigate = useNavigate();
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState({
    name: result?.visitor_name || "",
    email: "",
    phone: "",
    title: "",
    organization: "",
    district: "",
    grades_requested: [],
    estimated_ell_students: "",
    message: "",
    is_decision_maker: true,
    decision_maker_name: "",
    decision_maker_title: "",
    decision_maker_email: "",
  });

  const toggleGrade = (g) => {
    setLead((p) => ({
      ...p,
      grades_requested: p.grades_requested.includes(g)
        ? p.grades_requested.filter((x) => x !== g)
        : [...p.grades_requested, g],
    }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !lead.organization || !lead.title) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!lead.is_decision_maker && !lead.decision_maker_email) {
      toast.error("Please provide the decision maker's email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-leads`, {
        ...lead,
        demo_session_id: sessionId,
        demo_grade_tried: result?.grade_level,
        demo_wida_result: result?.overall_wida,
      });
      setLeadSubmitted(true);
      toast.success("Demo request submitted!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const wc = WIDA_COLORS[result?.wida_level] || WIDA_COLORS[1];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Results Header */}
      <div className="bg-[#002FA7] text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-blue-200 text-sm font-semibold mb-2">DEMO RESULTS</p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {result?.visitor_name}'s Language Proficiency Profile
          </h1>
          <p className="text-blue-100">Grade {result?.grade_level} Mini Assessment</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Overall Score Card */}
        <div className="bg-white border-2 border-gray-200 p-8 mb-8 text-center" data-testid="demo-results-card">
          <p className="text-sm text-gray-500 font-semibold mb-2">OVERALL COMPOSITE SCORE</p>
          <div className={`inline-flex items-center justify-center w-24 h-24 ${wc.bg} ${wc.text} text-4xl font-black mb-3`}>
            {result?.overall_wida}
          </div>
          <p className="text-xl font-bold">{result?.wida_level_name}</p>
          <p className="text-gray-500 text-sm mt-1">
            Proficiency Level {result?.wida_level} | {result?.overall_percentage}% correct
          </p>
          <p className="text-xs text-gray-400 mt-3">
            {result?.total_answered} of {result?.total_questions} questions answered
          </p>
        </div>

        {/* Modality Breakdown */}
        <div className="bg-white border-2 border-gray-200 p-6 mb-8">
          <h3 className="font-bold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Modality Breakdown
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {MODALITIES.map((mod) => {
              const s = result?.modality_scores?.[mod.key];
              const Icon = mod.icon;
              const level = s ? Math.max(1, Math.min(6, Math.round(s.wida))) : 1;
              const mc = WIDA_COLORS[level] || WIDA_COLORS[1];
              return (
                <div key={mod.key} className="text-center border border-gray-200 p-4" data-testid={`demo-result-${mod.key}`}>
                  <Icon size={20} className={`mx-auto mb-2 ${mod.color}`} />
                  <p className="text-xs text-gray-500 font-semibold">{mod.label}</p>
                  <div className={`inline-flex items-center justify-center w-12 h-12 ${mc.bg} ${mc.text} text-xl font-bold mt-2`}>
                    {s?.wida || "—"}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{s?.answered || 0}/{s?.total || 0} pts</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border-2 border-amber-300 p-4 mb-8 text-sm text-amber-800">
          <p className="font-semibold flex items-center gap-2 mb-1">
            <AlertCircle size={16} /> This is a demo assessment
          </p>
          <p>
            This mini assessment used {result?.total_questions} questions (vs. 63 in a full MOSAIC assessment).
            Speaking and Writing responses received estimated scores. Full assessments include
            teacher-graded rubrics for accurate proficiency levels.
          </p>
        </div>

        {/* CTA — Lead Form */}
        {!showLeadForm && !leadSubmitted && (
          <div className="bg-gradient-to-br from-[#002FA7] to-[#0040D0] text-white p-8 mb-8" data-testid="demo-lead-cta">
            <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Ready for Full Access?
            </h3>
            <p className="text-blue-100 mb-2">
              Unlock complete MOSAIC assessments for your school — all grades, all modalities,
              teacher dashboards, analytics, and printable reports.
            </p>
            <ul className="text-sm text-blue-100 space-y-1 mb-6">
              <li className="flex items-center gap-2"><CheckCircle size={14} /> 63 questions per grade (vs. {result?.total_questions} in this demo)</li>
              <li className="flex items-center gap-2"><CheckCircle size={14} /> Teacher grading for Speaking & Writing</li>
              <li className="flex items-center gap-2"><CheckCircle size={14} /> Adaptive difficulty per modality</li>
              <li className="flex items-center gap-2"><CheckCircle size={14} /> Growth tracking, goals, & PDF reports</li>
            </ul>
            <Button
              onClick={() => setShowLeadForm(true)}
              className="w-full h-14 text-lg bg-white text-[#002FA7] hover:bg-blue-50 font-bold"
              data-testid="demo-unlock-btn"
            >
              <Sparkles size={20} className="mr-2" />
              Request Full Grade Access
            </Button>
          </div>
        )}

        {/* Lead Form */}
        {showLeadForm && !leadSubmitted && (
          <div className="bg-white border-2 border-[#002FA7] p-6 sm:p-8 mb-8" data-testid="demo-lead-form">
            <h3 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <School size={20} className="text-[#002FA7]" />
              Request Full Demo Access
            </h3>
            <p className="text-sm text-gray-500 mb-6">Tell us about your school so we can activate your requested grades.</p>

            <form onSubmit={handleLeadSubmit} className="space-y-6">
              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold mb-1 block">Full Name *</Label>
                  <Input
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    placeholder="Jane Smith"
                    data-testid="lead-name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1 block">Title / Role *</Label>
                  <select
                    value={lead.title}
                    onChange={(e) => setLead({ ...lead, title: e.target.value })}
                    className="w-full h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none px-3 text-sm bg-white"
                    data-testid="lead-title"
                    required
                  >
                    <option value="">Select your role...</option>
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1 block">School Email *</Label>
                  <Input
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    placeholder="jane@school.nyc"
                    data-testid="lead-email"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1 block">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input
                    type="tel"
                    value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    placeholder="(212) 555-0100"
                    data-testid="lead-phone"
                  />
                </div>
              </div>

              {/* Organization */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold mb-1 block">School / Organization *</Label>
                  <Input
                    value={lead.organization}
                    onChange={(e) => setLead({ ...lead, organization: e.target.value })}
                    className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    placeholder="P.S. 124 Yung Wing"
                    data-testid="lead-org"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-1 block">District <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input
                    value={lead.district}
                    onChange={(e) => setLead({ ...lead, district: e.target.value })}
                    className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    placeholder="District 2"
                    data-testid="lead-district"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold mb-1 block">
                  Estimated ELL Students <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  value={lead.estimated_ell_students}
                  onChange={(e) => setLead({ ...lead, estimated_ell_students: e.target.value })}
                  className="h-11 border-2 border-gray-300 focus:border-[#002FA7] rounded-none max-w-xs"
                  placeholder="e.g., 150, 500+"
                  data-testid="lead-ell"
                />
              </div>

              {/* Grade Selection */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">
                  Which grade(s) would you like full demo access to? *
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="lead-grades">
                  {GRADES.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => toggleGrade(g.value)}
                      className={`p-3 border-2 text-center text-sm font-semibold transition-all ${
                        lead.grades_requested.includes(g.value)
                          ? "border-[#002FA7] bg-blue-50 text-[#002FA7]"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                      data-testid={`lead-grade-${g.value}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Decision Maker */}
              <div className="border-2 border-gray-200 p-5" data-testid="lead-dm-section">
                <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                  <UserCheck size={16} className="text-[#002FA7]" />
                  Who approves software purchases at your school?
                </Label>
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-3 border-2 cursor-pointer transition-colors ${
                    lead.is_decision_maker ? "border-[#002FA7] bg-blue-50" : "border-gray-200"
                  }`}>
                    <input
                      type="radio"
                      name="dm"
                      checked={lead.is_decision_maker}
                      onChange={() => setLead({ ...lead, is_decision_maker: true })}
                      className="accent-[#002FA7]"
                      data-testid="lead-dm-yes"
                    />
                    <span className="font-semibold text-sm">I am the decision maker</span>
                  </label>
                  <label className={`flex items-center gap-3 p-3 border-2 cursor-pointer transition-colors ${
                    !lead.is_decision_maker ? "border-[#002FA7] bg-blue-50" : "border-gray-200"
                  }`}>
                    <input
                      type="radio"
                      name="dm"
                      checked={!lead.is_decision_maker}
                      onChange={() => setLead({ ...lead, is_decision_maker: false })}
                      className="accent-[#002FA7]"
                      data-testid="lead-dm-no"
                    />
                    <span className="font-semibold text-sm">Someone else approves purchases</span>
                  </label>

                  {!lead.is_decision_maker && (
                    <div className="ml-6 mt-2 space-y-3 animate-fade-in-up" style={{ animationDuration: "0.2s" }}>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-semibold mb-1 block">Decision Maker Name *</Label>
                          <Input
                            value={lead.decision_maker_name}
                            onChange={(e) => setLead({ ...lead, decision_maker_name: e.target.value })}
                            className="h-10 border-2 border-gray-300 focus:border-[#002FA7] rounded-none text-sm"
                            placeholder="Dr. Robert Chen"
                            data-testid="lead-dm-name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold mb-1 block">Title</Label>
                          <Input
                            value={lead.decision_maker_title}
                            onChange={(e) => setLead({ ...lead, decision_maker_title: e.target.value })}
                            className="h-10 border-2 border-gray-300 focus:border-[#002FA7] rounded-none text-sm"
                            placeholder="Principal"
                            data-testid="lead-dm-title"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1 block">Decision Maker Email *</Label>
                        <Input
                          type="email"
                          value={lead.decision_maker_email}
                          onChange={(e) => setLead({ ...lead, decision_maker_email: e.target.value })}
                          className="h-10 border-2 border-gray-300 focus:border-[#002FA7] rounded-none text-sm"
                          placeholder="rchen@school.nyc"
                          data-testid="lead-dm-email"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-sm font-semibold mb-1 block">
                  Message <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  value={lead.message}
                  onChange={(e) => setLead({ ...lead, message: e.target.value })}
                  className="border-2 border-gray-300 focus:border-[#002FA7] rounded-none resize-none min-h-[80px]"
                  placeholder="Tell us about your school's ELL population and assessment needs..."
                  data-testid="lead-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-14 text-lg btn-sharp-primary"
                data-testid="lead-submit-btn"
              >
                {submitting ? (
                  <><div className="loading-spinner w-5 h-5 mr-2 border-white border-t-transparent" /> Submitting...</>
                ) : (
                  <><Send size={18} className="mr-2" /> Request Grade Access</>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Lead Submitted Confirmation */}
        {leadSubmitted && (
          <div className="bg-green-50 border-2 border-green-300 p-8 mb-8 text-center" data-testid="demo-lead-success">
            <CheckCircle size={40} className="text-green-600 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Request Submitted!
            </h3>
            <p className="text-gray-600 mb-1">
              We'll activate <strong>{lead.grades_requested.length > 0 ? `Grade ${lead.grades_requested.join(", ")}` : "your requested grades"}</strong> for{" "}
              <strong>{lead.organization}</strong>.
            </p>
            <p className="text-sm text-gray-500">Expect to hear from us within 1-2 business days.</p>
          </div>
        )}

        {/* Bottom CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/features" className="flex-1">
            <Button variant="outline" className="w-full h-12 btn-sharp" data-testid="demo-explore-features">
              <Sparkles size={16} className="mr-2" /> Explore All Features
            </Button>
          </Link>
          <Button
            onClick={() => navigate("/try-demo")}
            variant="outline"
            className="flex-1 h-12 btn-sharp"
            data-testid="demo-try-another"
          >
            <Play size={16} className="mr-2" /> Try Another Grade
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main DemoExam Page ─────────────────────────────────────────
const DemoExam = () => {
  const [phase, setPhase] = useState("setup"); // setup | instructions | exam | results
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [gradeLevel, setGradeLevel] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleStart = async (grade, name) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo-exam/start`, {
        grade_level: grade,
        visitor_name: name,
      });
      if (res.data.error || !res.data.questions?.length) {
        toast.error("No questions available for this grade. Try another.");
        setLoading(false);
        return;
      }
      setSessionId(res.data.session_id);
      setQuestions(res.data.questions);
      setGradeLevel(grade);
      setVisitorName(name);
      setPhase("instructions");
    } catch {
      toast.error("Failed to start demo exam. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBeginExam = () => {
    setPhase("exam");
  };

  const handleExamComplete = async (responses) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo-exam/${sessionId}/score`, { responses });
      setResult(res.data);
      setPhase("results");

      // Track A/B completion
      const abStored = localStorage.getItem("mosaic_ab_hero_cta");
      if (abStored) {
        try {
          const variant = JSON.parse(abStored);
          axios.post(`${API}/ab/track`, {
            experiment_id: "hero_cta_v1",
            variant_id: variant.variant_id,
            event: "completion",
            visitor_id: localStorage.getItem("mosaic_visitor_id") || "anonymous",
          }).catch(() => {});
        } catch {}
      }
    } catch {
      toast.error("Failed to score exam. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-500 font-semibold">
            {phase === "setup" ? "Preparing your demo exam..." : "Calculating your proficiency score..."}
          </p>
        </div>
      </div>
    );
  }

  if (phase === "setup") return <GradePickerPhase onStart={handleStart} />;
  if (phase === "instructions") return <PreTestInstructions gradeLevel={gradeLevel} studentName={visitorName} onBeginExam={handleBeginExam} isDemo={true} />;
  if (phase === "exam") return <ExamPhase questions={questions} gradeLevel={gradeLevel} visitorName={visitorName} onComplete={handleExamComplete} />;
  if (phase === "results") return <ResultsPhase result={result} sessionId={sessionId} />;

  return null;
};

export default DemoExam;
